import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type { ApiError, AuditAnchor, AuditCursor, AuditEntry } from '../api';
import { auditEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';
import { dateWords } from '../terms';

/**
 * Admin → Audit (D-195).
 *
 * `audit_log` has been written since migration 0001 and, until now, read only
 * by tests. The permission to read it has existed just as long.
 *
 * **The filters here are the API's indexes, spelled out.** One anchor at a
 * time, because each is a separate index and a combination is served by none
 * of them — so the form offers a choice of anchor rather than a row of boxes
 * that can be combined into a query the database would have to scan for. The
 * table only grows, and this is a screen somebody opens to look around.
 *
 * Every filter is in the URL, which makes any view of the log a link: a
 * question worth asking twice is worth being able to send to somebody.
 */

const ANCHORS = [
  { kind: 'recent', label: 'Everything, most recent first' },
  { kind: 'action', label: 'A kind of action' },
  { kind: 'target', label: 'One thing (type and id)' },
  { kind: 'actor', label: 'One person' },
  { kind: 'order', label: 'One order' },
  { kind: 'event', label: 'One event' },
] as const;

type AnchorKind = (typeof ANCHORS)[number]['kind'];

/** What changed, as a short list, rather than a wall of JSON. */
function Changes({ before, after }: { before: string | null; after: string | null }) {
  const [open, setOpen] = useState(false);
  if (!before && !after) return <span className="text-ink-400">—</span>;

  let summary = '';
  try {
    const b = before ? (JSON.parse(before) as Record<string, unknown>) : {};
    const a = after ? (JSON.parse(after) as Record<string, unknown>) : {};
    const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
    summary = keys.length > 0 ? keys.slice(0, 4).join(', ') + (keys.length > 4 ? ` +${keys.length - 4}` : '') : 'no field changed';
  } catch {
    // Malformed JSON should never reach here (the column has a json_valid
    // CHECK), but a viewer that throws on one bad row is worse than one that
    // shows the row and says so.
    summary = 'unreadable';
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(!open)} className="text-left text-accent hover:underline">
        {summary}
      </button>
      {open ? (
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          <pre className="max-h-64 overflow-auto bg-mist p-2 text-xs whitespace-pre-wrap">{before ? JSON.stringify(JSON.parse(before), null, 2) : '(nothing before)'}</pre>
          <pre className="max-h-64 overflow-auto bg-mist p-2 text-xs whitespace-pre-wrap">{after ? JSON.stringify(JSON.parse(after), null, 2) : '(nothing after)'}</pre>
        </div>
      ) : null}
    </>
  );
}

export default function AuditPage() {
  const can = usePermissions();
  const [params, setParams] = useSearchParams();

  const kind = (params.get('kind') as AnchorKind | null) ?? 'recent';
  const value = params.get('value') ?? '';
  const targetType = params.get('target_type') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  // Typed into, then committed — so a half-written id does not fire a request
  // per keystroke against a table this size.
  const [draft, setDraft] = useState(value);
  const [draftType, setDraftType] = useState(targetType);
  useEffect(() => {
    setDraft(value);
    setDraftType(targetType);
  }, [value, targetType]);

  const [pages, setPages] = useState<AuditEntry[][]>([]);
  const [cursor, setCursor] = useState<AuditCursor | null>(null);
  const key = `${kind}|${value}|${targetType}|${from}|${to}`;
  useEffect(() => {
    setPages([]);
    setCursor(null);
  }, [key]);

  const ready = kind === 'recent' || (kind === 'target' ? Boolean(value && targetType) : Boolean(value));
  const anchor: AuditAnchor =
    kind === 'target'
      ? { kind: 'target', target_type: targetType, target_id: value }
      : kind === 'actor'
        ? { kind: 'actor', actor_id: value }
        : kind === 'action'
          ? { kind: 'action', action: value }
          : kind === 'order'
            ? { kind: 'order', order_id: value }
            : kind === 'event'
              ? { kind: 'event', event_id: value }
              : { kind: 'recent' };

  const page = useQuery<{ entries: AuditEntry[]; next: AuditCursor | null }, ApiError>({
    queryKey: ['audit', key, cursor?.before_id ?? ''],
    queryFn: () => auditEndpoints.list(anchor, { ...(from ? { from } : {}), ...(to ? { to } : {}) }, cursor),
    enabled: ready && can('audit.view'),
  });

  useEffect(() => {
    if (!page.data) return;
    setPages((current) => (current.some((p) => p[0]?.id === page.data.entries[0]?.id) ? current : [...current, page.data.entries]));
  }, [page.data]);

  if (!can('audit.view')) return <NotPermitted permission="audit.view" />;

  const entries = pages.flat();
  const apply = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };

  return (
    <div className="space-y-4">
      {page.error ? <ErrorNote>{page.error.message}</ErrorNote> : null}

      <Panel
        title="Audit"
        description="Everything the system recorded about who did what, from where, and what changed. The log cannot be edited or deleted — by anyone, including here."
      >
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Look at</span>
            <select className={`mt-1 ${INPUT}`} value={kind} onChange={(e) => apply({ kind: e.target.value, value: '', target_type: '' })} aria-label="What to look at">
              {ANCHORS.map((a) => (
                <option key={a.kind} value={a.kind}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>

          {kind === 'target' ? (
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Type</span>
              <input className={`mt-1 ${INPUT}`} value={draftType} onChange={(e) => setDraftType(e.target.value)} placeholder={`event, order, ${dateWords().one}…`} aria-label="Target type" />
            </label>
          ) : null}

          {kind !== 'recent' ? (
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{kind === 'action' ? 'Action (a trailing dot matches a family)' : 'Id'}</span>
              <input
                className={`mt-1 w-80 ${INPUT}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') apply({ value: draft, target_type: draftType });
                }}
                placeholder={kind === 'action' ? 'order. or order.exchanged' : '01a0…'}
                aria-label={kind === 'action' ? 'Action' : 'Id'}
              />
            </label>
          ) : null}

          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">From</span>
            <input type="date" className={`mt-1 ${INPUT}`} value={from.slice(0, 10)} onChange={(e) => apply({ from: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' })} aria-label="From" />
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">To</span>
            <input type="date" className={`mt-1 ${INPUT}`} value={to.slice(0, 10)} onChange={(e) => apply({ to: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' })} aria-label="To" />
          </label>

          {kind !== 'recent' ? <Button onClick={() => apply({ value: draft, target_type: draftType })}>Search</Button> : null}
        </div>

        {!ready ? (
          <p className="px-5 pb-4 text-sm text-ink-500">
            {kind === 'target' ? 'A target needs both a type and an id — that is how the index is built.' : 'Enter what to look for.'}
          </p>
        ) : null}
      </Panel>

      <Panel title={`${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}>
        <Table headers={['When', 'Action', 'Who', 'Where from', 'Target', 'What changed']}>
          {entries.length === 0 ? (
            <EmptyRow columns={6}>{page.isFetching ? 'Reading…' : 'Nothing recorded for that.'}</EmptyRow>
          ) : (
            entries.map((entry) => (
              <tr key={entry.id} className="border-b border-line align-top last:border-0">
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(entry.occurred_at)}</td>
                <td className="px-5 py-3">
                  <button type="button" onClick={() => apply({ kind: 'action', value: entry.action, target_type: '' })} className="font-mono text-xs text-accent hover:underline">
                    {entry.action}
                  </button>
                  {entry.reason ? <div className="text-xs text-ink-400">{entry.reason}</div> : null}
                </td>
                <td className="px-5 py-3 text-sm">
                  <div className="text-ink-900">{entry.actor_display_snapshot ?? entry.actor_id ?? '—'}</div>
                  <div className="text-xs text-ink-400">
                    <Badge tone="neutral">{entry.actor_type}</Badge>
                    {entry.approved_by_staff_account_id ? <span className="ml-1">approved by {entry.approved_by_staff_account_id}</span> : null}
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-ink-500">
                  <div>{entry.surface ?? '—'}</div>
                  {entry.ip_address ? <div>{entry.ip_address}</div> : null}
                  {entry.register_id ? <div>register {entry.register_id}</div> : null}
                </td>
                <td className="px-5 py-3 text-xs">
                  <button type="button" onClick={() => apply({ kind: 'target', value: entry.target_id, target_type: entry.target_type })} className="font-mono text-accent hover:underline">
                    {entry.target_type}
                  </button>
                  <div className="font-mono text-ink-400">{entry.target_id.slice(0, 13)}…</div>
                  {entry.order_id ? (
                    <Link to={`/orders/${entry.order_id}`} className="text-accent hover:underline">
                      order
                    </Link>
                  ) : null}
                </td>
                <td className="px-5 py-3 text-xs">
                  <Changes before={entry.before_json} after={entry.after_json} />
                </td>
              </tr>
            ))
          )}
        </Table>

        {page.data?.next ? (
          <div className="px-5 py-4">
            <Button onClick={() => setCursor(page.data!.next)} disabled={page.isFetching}>
              {page.isFetching ? 'Reading…' : 'Show more'}
            </Button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
